/**
 * AdvancedSettingsSection Component
 *
 * Section for advanced dubbing settings including LLM refinement.
 * Reads state from DubbingContext — no props required.
 */

import { memo } from 'react';
import { Settings, Sparkles, AlertCircle } from 'lucide-react';
import { LLMModelSelector } from '../../selectors';
import { CompactSelect } from '../../common/CompactSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { LLM_PROVIDERS } from '@/constants';
import { useDubbing } from '../DubbingContext.context';
import type { LLMProvider } from '@/types';

export const AdvancedSettingsSection = memo(function AdvancedSettingsSection() {
  const { t } = useTranslation();
  const {
    state,
    isUploading,
    isDubbing,
    llmModels,
    llmSelectedModel,
    handleEnableLlmRefinementChange,
    handleLlmProviderChange,
    handleLlmModelChange,
  } = useDubbing();

  const { mediaType, enableLlmRefinement, llmProvider } = state;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
        <Settings className="h-5 w-5" />
        <span>{t('dubbing.sections.advancedSettings', { defaultValue: 'Advanced Settings' })}</span>
      </h3>

      <div className="space-y-4">
        {/* LLM Translation Refinement */}
        <div className="space-y-4 p-4 border border-purple-200 rounded-lg bg-purple-50">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableLlmRefinement"
              checked={enableLlmRefinement}
              onChange={(e) => handleEnableLlmRefinementChange(e.target.checked)}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              disabled={isUploading || isDubbing}
            />
            <label
              htmlFor="enableLlmRefinement"
              className="text-sm font-medium text-gray-700 flex items-center space-x-2"
            >
              <Sparkles className="h-4 w-4 text-purple-600" />
              <span>
                {t('dubbing.fields.enableLlmRefinement', {
                  defaultValue: 'Enable AI Translation Refinement',
                })}
              </span>
            </label>
          </div>

          <p className="text-xs text-gray-600 ml-6">
            {t('dubbing.fields.llmRefinementDescription', {
              defaultValue:
                'Use an LLM to improve translation quality by fixing context loss, pronoun references, and ensuring natural phrasing. Uses free models by default.',
            })}
          </p>

          {/* LLM Provider and Model Selection (only shown when enabled) */}
          {enableLlmRefinement && (
            <div className="ml-6 mt-3 space-y-3 p-3 bg-white rounded border border-purple-100">
              {/* Provider Selector */}
              <CompactSelect
                label={t('dubbing.fields.llmProvider', { defaultValue: 'LLM Provider' })}
                options={LLM_PROVIDERS.map((provider) => ({
                  value: provider.value,
                  label: provider.label,
                  disabled: provider.disabled,
                }))}
                value={llmProvider}
                onChange={(value) => handleLlmProviderChange(value as LLMProvider)}
                disabled={isUploading || isDubbing}
                testId="llm-provider-select"
              />

              {/* Visual Model Selector with Avatars */}
              <LLMModelSelector
                models={
                  llmModels.length === 0
                    ? [{ value: '', label: 'Loading models...', disabled: true }]
                    : llmModels
                }
                value={llmSelectedModel}
                onChange={handleLlmModelChange}
                disabled={isUploading || isDubbing || llmModels.length === 0}
                label={t('dubbing.fields.llmModel', { defaultValue: 'AI Model' })}
                showFreeOnly={false}
              />
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              {/* Pick the KEY by media type. Selecting the media-specific
                  copy inside `defaultValue` did nothing: defaultValue is only
                  consulted when the key is missing from the locale, and both
                  of these keys exist — so audio users always read the video
                  text ("...synchronize them with your video"). */}
              <p className="font-medium mb-1">
                {t(mediaType === 'audio' ? 'dubbing.info.titleAudio' : 'dubbing.info.title')}
              </p>
              <p>
                {t(
                  mediaType === 'audio'
                    ? 'dubbing.info.descriptionAudio'
                    : 'dubbing.info.description'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
